// @flow
import React, { useState } from 'react';
import { find } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  SettingsMenu, EmptyState, PlatformSwitch, AppMobileHeader, ConditionalFeature, AppMobileFooter, DoctorSelector, ServiceTemplatePrice,
} from '@blocks';
import { Button, ClickableList, Dropdown, Spinner, SearchInput } from '@elements';
import type { User, BundledPermissions } from '@types';
import { sort, alphabeticCompare, filterSearch, isPresent, useTranslationNs } from '@helpers/helpers';
import { OFFLINE_SEARCH_EDGE } from '@helpers/constants';

export type TemplateItem = {
  linkTo?: string,
  preventClick?: boolean,
  customRendererData: {
    name: string,
    price: string,
    unit?: ?string,
    healthInsurance: boolean,
  },
};

type Props = {
  user: User,
  doctorId: number,
  doctorsData: any,
  doctorsList: Array<Object>,
  sharedItems: Array<TemplateItem>,
  doctorItems: Array<TemplateItem>,
  servicesLoading: boolean,
  isFeatureEnabled: boolean,
  onLogout: Function,
  permissions: BundledPermissions,
  // Managers change the shared services; the doctor's are also the doctor's and the secretaries'
  canEditShared: boolean,
  canEditDoctor: boolean,
  // Managers and secretaries see any professional's services, in a clinic with more than one
  canPickDoctor: boolean,
  pushPath: Function,
  onPrint: Function,
};

// Ajustes › Tabela de Serviços, as in the Painel de Controle: the services shared by the whole clinic, then the chosen
// professional's own. A plano de saúde service carries a badge instead of a list of its own.
const ServiceTemplatesList = ({
  user, doctorId, doctorsData, doctorsList, sharedItems, doctorItems, servicesLoading, isFeatureEnabled, onLogout,
  permissions, canEditShared, canEditDoctor, canPickDoctor, pushPath, onPrint,
}: Props) => {
  const [_] = useTranslationNs('settings');
  // _ has no interpolation
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDoctorSelector, setShowDoctorSelector] = useState(false);

  const selectedDoctor = find(doctorsList, item => parseInt(item ? item.id : 0, 10) === parseInt(doctorId, 10));
  const searching = isPresent(searchTerm);
  const visible = (items) => {
    // sort compares the customRendererData
    const sorted = sort(items, alphabeticCompare(['name']));
    return searching ? filterSearch(sorted, searchTerm, ['customRendererData', 'name']) : sorted;
  };

  const renderer = item => ([
    <span key="name" className="serviceTemplateName">
      {item.name}
      {item.healthInsurance && <span className="badge health_insurance serviceTemplateBadge">{_('label/status_health_insurance', 'global')}</span>}
    </span>,
    <span key="price"><ServiceTemplatePrice template={item} /></span>,
  ]);

  const section = ({
    testId, title, description, newText, newTo, canAdd, items, empty, emptyHint, notice,
  }) => {
    const shown = visible(items);
    let list;
    if (servicesLoading) list = <Spinner text={_('label/loading', 'global')} />;
    else if (shown.length) list = <ClickableList items={shown} customRenderer={renderer} card />;
    else if (searching) list = <EmptyState className="inlineEmptyStateMobile" title={_('label/services/search_empty')} />;
    else list = <EmptyState className="inlineEmptyStateMobile" title={empty} hint={emptyHint} />;

    return (
      <section className="serviceTemplatesSection" data-testid={testId}>
        <div className="toolbar dashed">
          <div>
            <h1>{`${title} (${items.length})`}</h1>
            <p className="serviceTemplatesDescription">{description}</p>
          </div>
          {canAdd && <Button className="primary" text={newText} linkTo={newTo} />}
        </div>
        {notice && <p className="serviceTemplatesNotice">{notice}</p>}
        {list}
      </section>
    );
  };

  const doctorPicker = canPickDoctor && (
    <Dropdown
      className="titleFilter withOpacity"
      items={[]}
      selectedItem={selectedDoctor}
      onSelectItem={id => pushPath(`/ajustes/servicos/${id}`)}
      onClick={() => setShowDoctorSelector(true)}
    />
  );

  const pageContent = (
    <React.Fragment>
      <DoctorSelector
        isVisible={showDoctorSelector}
        onClose={() => setShowDoctorSelector(false)}
        doctors={doctorsData}
        onSelect={id => pushPath(`/ajustes/servicos/${id}`)}
      />
      <div className="serviceTemplatesActions">
        {sharedItems.length + doctorItems.length > OFFLINE_SEARCH_EDGE && (
          <div className="searchBox-services-settings">
            {/* No mobileAutofocus: in the apps focusing on load opened the keyboard (the web still focuses it) */}
            <SearchInput debounced onChange={setSearchTerm} autofocus />
          </div>
        )}
        <Button className="linkButton serviceTemplatesPrint" text={_('label/services/print')} onClick={onPrint} />
      </div>
      <div className="firstList">
        {section({
          testId: 'shared-templates',
          title: _('label/services/shared/title'),
          description: _('label/services/shared/description'),
          newText: _('label/services/shared/new'),
          newTo: `/ajustes/servico/${doctorId}/novo?shared=true`,
          canAdd: canEditShared,
          items: sharedItems,
          empty: _('label/services/shared/empty'),
          emptyHint: _('label/services/shared/empty_hint'),
          notice: canEditShared ? null : _('label/services/shared/managers_only'),
        })}
      </div>
      {section({
        testId: 'doctor-templates',
        title: _('label/services/doctor/title'),
        description: !canPickDoctor && selectedDoctor
          ? t('settings:label/services/doctor/description_for', { name: selectedDoctor.text })
          : _('label/services/doctor/description'),
        newText: _('label/services/doctor/new'),
        newTo: `/ajustes/servico/${doctorId}/novo`,
        canAdd: canEditDoctor,
        items: doctorItems,
        empty: _('label/services/doctor/empty'),
        emptyHint: _('label/services/doctor/empty_hint'),
        notice: null,
      })}
    </React.Fragment>
  );

  return (
    <PlatformSwitch
      inject
      desktop={() => (
        <div className="appContent settingsPage serviceTemplatesPage">
          <aside className="sidebar">
            <SettingsMenu user={user} onLogout={onLogout} permissions={permissions} />
          </aside>
          <section className="mainSection">
            {isFeatureEnabled && doctorPicker && <div className="topBar">{doctorPicker}</div>}
            <ConditionalFeature enabled={isFeatureEnabled} transparent>
              <div className="centerContent">
                {pageContent}
              </div>
            </ConditionalFeature>
          </section>
        </div>
      )}
      mobile={() => (
        <React.Fragment>
          <AppMobileHeader title={_('action/menu/sevices_list')} backLinkTo="/ajustes" />
          <div className="appContent settingsPage serviceTemplatesPage">
            <ConditionalFeature enabled={isFeatureEnabled} transparent>
              {isFeatureEnabled && doctorPicker && <div className="topBar mobile">{doctorPicker}</div>}
              <section className="mainSection">
                <div className="centerContent">
                  {pageContent}
                </div>
              </section>
            </ConditionalFeature>
          </div>
          <AppMobileFooter />
        </React.Fragment>
      )}
    />
  );
};

export default ServiceTemplatesList;
